import moment from "moment";

import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { MyleNoteDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { PortalStatusProps } from "@/Common/Components/Portal/PortalStatusIcon";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { GenderIdentity as MyleGenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns as MylePreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { IJPortalLinkedItemModel } from "@/Common/Models/Portal/JSONInterfaces/IJPortalLinkedItemModel";
import { GetPatientOverviewResponse, MailingAddressDto, PatientProfileDto, SubscriptionPaymentDto, TelephoneNumberDto, UserPermissionFlags } from "@/Common/Models/Portal/MedfarPortal";
import { ContactType, GenderIdentity, MedicalSex, PaymentStatus, PaymentType, PreferredPronouns, TelephoneNumberType, TransactionStatus } from "@/Common/Models/Portal/MedfarPortalEnums";
import { FormattedPhoneNumber } from "@/Common/Models/Portal/Patient/FormattedPhoneNumber";
import { PatientProfileMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { PortalStatusCheckable } from "@/Common/Models/Portal/PortalStatusCheckable";
import { LinkedItemNote } from "@/Common/Models/Portal/SharedItem/LinkedItem";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";

class PortalHelper {
    public static getDefaultPortalLinkedItemModel(): IJPortalLinkedItemModel {
        var portalLinkedItem: IJPortalLinkedItemModel =
        {
            LinkedItemType: null,
            LinkedItemId: "",
            LinkedItemSiteId: "",
            LinkedItemModel: null,
            LinkedItemMessages: [],
            SendButtonText: "",
            ExtraButtonText: "",
            ExtraButtonOperation: null,
            ItemDescription: "",
            NewMessageText: "",
            IsSending: false,
            SharedItem: null
        };

        return portalLinkedItem;
    }

    public static shouldShowPortalShareButton(patients: IndividualLight[]): boolean {
        if (patients && patients.length === 1) {
            if (cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) {
                return true;
            }

            return (patients[0].PortalSyncState &&
                    patients[0].PortalSyncState !== PortalRegistrationState.PortalRefused &&
                    patients[0].PortalSyncState !== PortalRegistrationState.Default);
        }

        return false;
    }

    public static getPortalButtonPropsForRegistrationState(
        registrationState: PortalRegistrationState,
        showIfDefault: boolean = false
    ): PortalStatusProps | null {
        switch (registrationState) {
            case PortalRegistrationState.RegistrationComplete:
                return {
                    background: "blue",
                    badge: "ok"
                };

            case PortalRegistrationState.PortalRefused:
                return {
                    badge: "refused"
                };

            case PortalRegistrationState.ChildAccount:
            case PortalRegistrationState.RegistrationPending:
                return {
                    background: "blue",
                    badge: "pending"
                };

            case PortalRegistrationState.InvitationLocked:
                return {
                    badge: "locked"
                };

            default:
                return showIfDefault ? {} : null;
        }
    }

    public static getPortalButtonPropsForSyncStatus(
        syncStatus: PortalSyncStatus,
        includeLinkedItems: boolean,
        hasBeenModifiedSinceLastShared?: boolean,
        hasBeenSharedOnPreviousEncounter?: boolean,
        isSharedItem?: boolean
    ): PortalStatusProps {
        let props: PortalStatusProps = {};

        let isShared = false;
        let isRevoked = false;
        switch (syncStatus) {
            case PortalSyncStatus.Pending:
                props.badge = "pending";
                isShared = true;
                break;

            case PortalSyncStatus.Viewed:
                props.badge = "ok";
                isShared = true;
                break;

            case PortalSyncStatus.Revoked:
                props.badge = "revoked";
                isRevoked = true;
                break;

            case PortalSyncStatus.Locked:
                props.badge = "locked";
                break;

            case PortalSyncStatus.Error:
                props.badge = "error";
                break;
        }

        if (isRevoked)
            return props;

        if (isShared && includeLinkedItems) {
            props.background = hasBeenModifiedSinceLastShared
                ? "orange"
                : "blue";
        }
        else if (!isShared && hasBeenSharedOnPreviousEncounter) {
            props.background = hasBeenModifiedSinceLastShared
                ? "lightOrange"
                : "lightBlue";
        }

        if (isSharedItem)
            props.icon = "share";

        return props;
    }

    public static getPortalButtonPropsForShareableItem(
        shareableItem: IPortalShareableItem
    ): PortalStatusProps {
        return this.getPortalButtonPropsForSyncStatus(
            shareableItem.getGlobalPortalStatus(),
            shareableItem.getSharedItem()?.IncludeLinkedItem,
            shareableItem.hasBeenModifiedSinceLastShared(),
            shareableItem.hasBeenSharedOnPreviousEncounter(),
            true
        );
    }

    public static isStatusVisible(status: PortalSyncStatus): boolean {
        return status === PortalSyncStatus.Pending
            || status === PortalSyncStatus.Viewed
            || status === PortalSyncStatus.Error;
    }

    public static getLinkedItemPortalStatus(sharedItem: SharedItem): PortalSyncStatus {
        if (!sharedItem || sharedItem.Status === PortalSyncStatus.Unsent)
            return PortalSyncStatus.Unsent;

        // return the shared item status if the item is currently visible
        if (this.isStatusVisible(sharedItem.Status) && sharedItem.IncludeLinkedItem)
            return sharedItem.Status;

        // else return revoked/unsent depending on previous visibility
        return sharedItem.HasPreviouslyIncludedItem
            ? PortalSyncStatus.Revoked
            : PortalSyncStatus.Unsent;
    }

    public static getGlobalPortalStatus(
        sharedItem: SharedItem,
        sharedItemMessages: SharedItemMessage[]
    ): PortalSyncStatus {
        if (!sharedItem || sharedItem.Status === PortalSyncStatus.Unsent)
            return PortalSyncStatus.Unsent;

        // if the shared item is viewed, return pending if any messages are pending
        if (sharedItem.Status === PortalSyncStatus.Viewed
            && sharedItemMessages?.any(p => p.Status === PortalSyncStatus.Pending))
            return PortalSyncStatus.Pending;

        return sharedItem.Status;
    }

    public static getLabResultPortalMessages(portalMessages: SharedItemMessage[]): SharedItemMessage[] {
        let portalCovertMessages: SharedItemMessage[] = [];

        if (portalMessages != null && portalMessages.length > 0) {
            portalCovertMessages = portalMessages.map(x => {
                x.tmpIdForUI = SharedItemMessage.getTempIdForUi(x);
                return x;
            });
        }
        return portalCovertMessages;
    }

    public static getPortalMessageOfType(portalMessages: SharedItemMessage[], type: PortalLinkedItemType): SharedItemMessage[] {
        let result: SharedItemMessage[] = [];

        if (portalMessages != null && portalMessages.length > 0) {
            result = portalMessages.filter((x: SharedItemMessage) => x.MessageType == type);
        }

        return result;
    }

    public static convertLinkedItemNotesToPortalMessage(itemNotes: LinkedItemNote[], messageType: PortalLinkedItemType): SharedItemMessage[] {
        if (!itemNotes) return [];

        return itemNotes.map(itemNote => new SharedItemMessage({
            Text: itemNote.Note,
            SentByContactId: itemNote.EmployeeContactId,
            DateCreated: itemNote.Timestamp,
            CopiedFromId: itemNote.NoteId,
            MessageType: messageType,
            Status: PortalSyncStatus.Unsent,
            LastStatusDate: moment().toDate(),
            CreatedByUserId: sessionStore.userId,
            DateModified: moment().toDate(),
            IsSelected: false,
            IsDisabled: false
        }));
    }

    public static convertLabResultNotesToPortalMessage(labNotes: MyleNoteDTO[]): SharedItemMessage[] {
        if (!labNotes) return [];

        return labNotes.map(note => new SharedItemMessage({
            Text: note.Comment,
            SentByContactId: cacheStore.GetEmployeeByContactFacetId(note.EmployeeId).Contact_id,
            DateCreated: note.TimeStamp,
            CopiedFromId: note.Id,

            Status: PortalSyncStatus.Unsent,
            LastStatusDate: moment().toDate(),
            CreatedByUserId: sessionStore.userId,
            DateModified: moment().toDate(),
            IsSelected: false,
            IsDisabled: false
        }));
    }

    public static getPortalStatusCheckableList(target: number): PortalStatusCheckable[] {
        return [
            new PortalStatusCheckable({
                Id: PortalSyncStatus.Unsent.toString(),
                Checked: true,
                Name: "NotSentLabel",
                Target: target
            }),
            new PortalStatusCheckable({
                Id: PortalSyncStatus.Pending.toString(),
                Checked: true,
                Name: "PendingLabel",
                Target: target
            }),
            new PortalStatusCheckable({
                Id: PortalSyncStatus.Viewed.toString(),
                Checked: true,
                Name: "ViewedLabel",
                Target: target
            }),
            new PortalStatusCheckable({
                Id: PortalSyncStatus.Revoked.toString(),
                Checked: true,
                Name: "RevokedLabel",
                Target: target
            }),
            new PortalStatusCheckable({
                Id: PortalSyncStatus.Error.toString(),
                Checked: true,
                Name: "InErrorLabel",
                Target: target
            })
        ];
    }

    public static getPortalContactTypeLocalizedString(portalContactType: ContactType): string {
        // contactpoint lookups
        let otherType = cacheStore.FindLookupByLookupCode("OTHERCONTACTPOINT", "CONTACTPOINTTYPE");
        let workType = cacheStore.FindLookupByLookupCode("WORK", "CONTACTPOINTTYPE");
        let personalType = cacheStore.FindLookupByLookupCode("PERSONAL", "CONTACTPOINTTYPE");

        if (portalContactType == ContactType.Work || portalContactType.toString() == ContactType[ContactType.Work]) {
            return cacheStore.GetLocalizedValueByLookupId(workType.LookupID);
        }
        else if (portalContactType == ContactType.Personal || portalContactType.toString() == ContactType[ContactType.Personal]) {
            return cacheStore.GetLocalizedValueByLookupId(personalType.LookupID);
        }
        else if (portalContactType == ContactType.Other || portalContactType.toString() == ContactType[ContactType.Other]) {
            return cacheStore.GetLocalizedValueByLookupId(otherType.LookupID);
        }
        else if (portalContactType == ContactType.Othercontactpoint || portalContactType.toString() == ContactType[ContactType.Othercontactpoint]) {
            return cacheStore.GetLocalizedValueByLookupId(otherType.LookupID);
        }
        else
            return cacheStore.GetString("UnknownLabel");
    }

    public static getPortalTelephoneTypeLocalizedString(telType: TelephoneNumberType): string {
        // phone type lookups
        let cellType = cacheStore.FindLookupByLookupCode("MOBILE", "PHONETYPE");
        let phoneType = cacheStore.FindLookupByLookupCode("PHONE", "PHONETYPE");
        let pagerType = cacheStore.FindLookupByLookupCode("PAGER", "PHONETYPE");
        let faxType = cacheStore.FindLookupByLookupCode("FAX", "PHONETYPE");

        if (telType == TelephoneNumberType.Fax || telType.toString() == TelephoneNumberType[TelephoneNumberType.Fax]) {
            return cacheStore.GetLocalizedValueByLookupId(faxType.LookupID);
        }
        else if (telType == TelephoneNumberType.Mobile || telType.toString() == TelephoneNumberType[TelephoneNumberType.Mobile]) {
            return cacheStore.GetLocalizedValueByLookupId(cellType.LookupID);
        }
        else if (telType == TelephoneNumberType.Phone || telType.toString() == TelephoneNumberType[TelephoneNumberType.Phone]) {
            return cacheStore.GetLocalizedValueByLookupId(phoneType.LookupID);
        }
        else if (telType == TelephoneNumberType.Pager || telType.toString() == TelephoneNumberType[TelephoneNumberType.Pager]) {
            return cacheStore.GetLocalizedValueByLookupId(pagerType.LookupID);
        }
        else
            return cacheStore.GetString("UnknownLabel");
    }

    public static convertPortalPatientsToMylePersons(portalPatients: PatientProfileDto[]): Person[] {
        let result: Person[] = [];
        result = portalPatients
            .map((p: PatientProfileMyleDto) => Person.FromPortalPatientProfile(p));
        return result;
    }

    public static hasAccessToOtherProfiles(portalPatientOverview: GetPatientOverviewResponse, patientId: string): boolean {
        return !!(portalPatientOverview &&
            portalPatientOverview.Account &&
            portalPatientOverview.Account.SharedPatients &&
            portalPatientOverview.Account.SharedPatients.length > 0 &&
            portalPatientOverview.Account.SharedPatients.filter(x => x.MyleId != patientId).length > 0);
    }

    public static hasGrantedAccessToOtherProfiles(portalPatientOverview: GetPatientOverviewResponse): boolean {
        return !!(portalPatientOverview &&
            portalPatientOverview.LinkedAccounts &&
            portalPatientOverview.LinkedAccounts.length > 0 &&
            portalPatientOverview.LinkedAccounts.filter(x => !x.IsOwner).length > 0);
    }

    public static getFormattedPhoneNumber(phone: TelephoneNumberDto, validationHelper: ValidationHelper): FormattedPhoneNumber {
        let result = new FormattedPhoneNumber();
        let phoneCountryLookupCode = cacheStore.GetCountryLookupCodeByISOCodeAlpha2(phone.CountryCode);
        let phoneCountryLookup: FlatLookupView;
        if (phoneCountryLookupCode == null)
            phoneCountryLookup = cacheStore.FindLookupByLookupCode("CNCANADA"); // default code = canada;
        else
            phoneCountryLookup = cacheStore.FindLookupByLookupCode(phoneCountryLookupCode);

        let contactType = PortalHelper.getPortalContactTypeLocalizedString(phone.ContactType);
        let phoneType = PortalHelper.getPortalTelephoneTypeLocalizedString(phone.NumberType);

        let validationRule = validationHelper.getPhoneNumberValidationRule(phoneCountryLookup.LookupID, null); // no need for region when checking for phone number format?
        let displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "";
        let displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "";
        let formattedPhone = ValidationHelper.cleanPhoneNumber(phone.Number, phoneCountryLookup.LookupID);
        formattedPhone = validationHelper.getFormatedString(formattedPhone, displayFormatSelector, displayFormatSubstitution);

        result.FormattedPhoneNumber = formattedPhone;
        result.ContactType = contactType;
        result.PhoneType = phoneType;

        return result;
    }

    public static getFormattedPostalCode(address: MailingAddressDto, validationHelper: ValidationHelper): string {
        let result = "";
        let addressLookupCode = cacheStore.GetCountryLookupCodeByISOCodeAlpha2(address.CountryCode);
        let addressCountryLookup: FlatLookupView;
        if (addressLookupCode == null)
            addressCountryLookup = cacheStore.FindLookupByLookupCode("CNCANADA"); // default code = canada;
        else
            addressCountryLookup = cacheStore.FindLookupByLookupCode(addressLookupCode);

        let validationRule = validationHelper.getPostalCodeValidationRule(addressCountryLookup.LookupID, null); // no need for region when checking for phone number format?
        let displayFormatSelector: string = validationRule ? validationRule.DisplayFormatSelectorPatern : "([ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ])([0-9][ABCEGHJKLMNPRSTVWXYZ][0-9])";
        let displayFormatSubstitution: string = (validationRule) ? validationRule.DisplayFormatSubstitution : "$1 $2";
        result = validationHelper.getFormatedString(address.PostCode, displayFormatSelector, displayFormatSubstitution);
        return result;
    }

    public static getFormattedAddress(address: MailingAddressDto, validationHelper: ValidationHelper, includeCity: boolean): string {
        let result = "";

        if (address != null) {
            if (address.StreetAddress != null && address.StreetAddress.length > 0)
                result = address.StreetAddress;
            if (address.Company != null && address.Company.length > 0)
                result += ", " + address.Company;

            if (includeCity == true && address.City != null && address.City.length > 0)
                result += ", " + address.City;

            if (address.Province != null && address.Province.length > 0)
                result += ", " + address.Province;

            if (address.CountryCode != null && address.CountryCode.length > 0)
                result += ", " + address.CountryCode;

            if (address.PostCode != null && address.PostCode.length > 0)
                result += ", " + PortalHelper.getFormattedPostalCode(address, validationHelper);
        }

        if (result.length <= 0)
            result = "Ø " + cacheStore.GetString("AddressLabel");

        return result;
    }

    public static getPaymentStatusLabel(e: PaymentStatus): string {
        switch (e) {
            case PaymentStatus.Paid:
                return cacheStore.GetString("PaidLabel");
            case PaymentStatus.Unpaid:
                return cacheStore.GetString("UnpaidLabel");
            case PaymentStatus.Pending:
                return cacheStore.GetString("PendingLabel");
            case PaymentStatus.ReviewRequired:
                return cacheStore.GetString("ReviewRequiredLabel");
            case PaymentStatus.RefundPending:
                return cacheStore.GetString("RefundPendingLabel");
            case PaymentStatus.Refunded:
                return cacheStore.GetString("RefundedLabel");
            case PaymentStatus.RefundPaid:
                return cacheStore.GetString("RefundPaidLabel");
            default:
                return "-";
        }
    }

    public static getTransactionDisplayString(trx: SubscriptionPaymentDto): string {
        let result = "N/A";

        if (trx != null && trx.PaymentGatewayName != null) {
            result = trx.PaymentGatewayName + " - " + trx.PaymentGatewayTransactionId;
        }

        return result;
    }

    public static getPaymentStatusClass(e: PaymentStatus): string {
        switch (e) {
            case PaymentStatus.Paid:
            case PaymentStatus.RefundPaid:
            case PaymentStatus.Refunded:
                return "green-text";

            case PaymentStatus.Unpaid:
            case PaymentStatus.ReviewRequired:
                return "red-text";

            default:
                return "";
        }
    }

    public static getPaymentType(e: PaymentType): string {
        switch (e) {
            case PaymentType.PayPal:
                return "PayPay";
            case PaymentType.Google:
                return "Google";
            case PaymentType.Apple:
                return "Apple";
            case PaymentType.BankAccount:
                return "Banque";
            case PaymentType.Mastercard:
                return "Mastercard";
            case PaymentType.Visa:
                return "Visa";
            case PaymentType.Amex:
                return "Amex";
            case PaymentType.Discover:
                return "Discover";
            case PaymentType.DinersClub:
                return "DinersClub";
            case PaymentType.Jcb:
                return "Jcb";
            default:
                return "";
        }
    }

    public static getTransactionStatusDisplay(e: TransactionStatus): string {
        switch (e) {
            case TransactionStatus.Unknown:
                return cacheStore.GetString("UnknownLabel");
            case TransactionStatus.Pending:
                return cacheStore.GetString("PendingLabel");
            case TransactionStatus.Settled:
                return cacheStore.GetString("SettledLabel");
            case TransactionStatus.Declined:
                return cacheStore.GetString("DeclinedLabel");
            case TransactionStatus.RefundPending:
                return cacheStore.GetString("RefundPendingLabel");
            case TransactionStatus.Refunded:
                return cacheStore.GetString("RefundedLabel");
            case TransactionStatus.Voided:
                return cacheStore.GetString("VoidedLabel");
            case TransactionStatus.ReviewRequired:
                return cacheStore.GetString("ReviewRequiredLabel");
            case TransactionStatus.FailedReview:
                return cacheStore.GetString("FailedReviewLabel");
            case TransactionStatus.Expired:
                return cacheStore.GetString("ExpiredLabel");
            case TransactionStatus.Error:
                return cacheStore.GetString("InErrorLabel");
            case TransactionStatus.Tentative:
                return cacheStore.GetString("TentativeLabel");
            case TransactionStatus.New:
                return cacheStore.GetString("NewLabel");
            default:
                return "";
        }
    }

    public static getSharedMessageIdsToRevoke(portalStatusModel: IPortalLinkedItemModel): number[] {
        let messageIdsToRevoke: number[] = [];
        if (portalStatusModel.get("LinkedItemMessages") != null) {
            let allPortalMessages = portalStatusModel.get("LinkedItemMessages");

            // revoke only pending messages
            allPortalMessages.map(x => {
                if (x.Status === PortalSyncStatus.Pending ||
                    x.Status === PortalSyncStatus.Viewed ||
                    x.Status === PortalSyncStatus.Error ||
                    x.Status === PortalSyncStatus.Unknown) {
                    messageIdsToRevoke.push(+x.SharedItemMessageId);
                }
            });
        }

        return messageIdsToRevoke;
    }

    public static getAllSharedPatientPermissionFlags(): UserPermissionFlags {
        return (UserPermissionFlags.Dossier |
            UserPermissionFlags.Document |
            UserPermissionFlags.Message |
            UserPermissionFlags.Profile |
            UserPermissionFlags.Appointment |
            UserPermissionFlags.Billing);
    }

    public static genderEnumToLookupId(enumValue: MedicalSex): string {
        cacheStore.FindLookupByLookupCode("MALE", "Gender");
        switch (enumValue) {
            case MedicalSex.Female:
                return cacheStore.FindLookupByLookupCode("FEMALE", "Gender").LookupID;
                break;

            case MedicalSex.Male:
                return cacheStore.FindLookupByLookupCode("MALE", "Gender").LookupID;
                break;

            case MedicalSex.Other:
                return cacheStore.FindLookupByLookupCode("OTHERGENDER", "Gender").LookupID;
                break;

            default:
                return "";
        }
    }

    public static convertPortalGenderIdentity(genderIdentity: GenderIdentity): MyleGenderIdentity {
        switch (genderIdentity) {
            case GenderIdentity.Male:
                return MyleGenderIdentity.Male;

            case GenderIdentity.Female:
                return MyleGenderIdentity.Female;

            case GenderIdentity.TransMale:
                return MyleGenderIdentity.TransMale;

            case GenderIdentity.TransFemale:
                return MyleGenderIdentity.TransFemale;

            case GenderIdentity.NonBinary:
                return MyleGenderIdentity.NonBinary;

            case GenderIdentity.Genderqueer:
                return MyleGenderIdentity.Genderqueer;

            case GenderIdentity.Genderfluid:
                return MyleGenderIdentity.Genderfluid;

            case GenderIdentity.TwoSpirit:
                return MyleGenderIdentity.TwoSpirit;

            case GenderIdentity.Agender:
                return MyleGenderIdentity.Agender;

            case GenderIdentity.PrefersNotToSay:
                return MyleGenderIdentity.PrefersNotToSay;

            case GenderIdentity.Other:
                return MyleGenderIdentity.Other;

            default:
                return null;
        }
    }

    public static convertPortalPreferredPronouns(preferredPronouns: PreferredPronouns): MylePreferredPronouns {
        switch (preferredPronouns) {
            case PreferredPronouns.He:
                return MylePreferredPronouns.He;

            case PreferredPronouns.She:
                return MylePreferredPronouns.She;

            case PreferredPronouns.They:
                return MylePreferredPronouns.They;

            case PreferredPronouns.Other:
                return MylePreferredPronouns.Other;

            default:
                return null;
        }
    }

    public static getPrefixLookupIdFromGenderAndBirthDate(gender: MedicalSex, dob: Date): string {
        if (gender == null || gender == MedicalSex.Unknown) {
            return null;
        }
        else if (gender == MedicalSex.Male) {
            return cacheStore.FindLookupByLookupCode("PREFIX.MR", "COMMON.INDIVIDUAL.PREFIXES").LookupID;
        }
        else if (gender == MedicalSex.Female) {
            if (dob == null || moment().diff(moment(dob), "days") <= 18 * 365) {
                return cacheStore.FindLookupByLookupCode("PREFIX.MISS", "COMMON.INDIVIDUAL.PREFIXES").LookupID;
            }
            else {
                return cacheStore.FindLookupByLookupCode("PREFIX.MS", "COMMON.INDIVIDUAL.PREFIXES").LookupID;
            }
        }
    }

    public static showWarningToSyncToPortal(sharedItem: SharedItem, requestingStaffId: string, createdbyId: string, signPrintAuthorization: SignedPrintAuthorization | null): boolean {
        const currentEmployee = sessionStore.getCurrentEmployee();
        const isSynced = !!sharedItem;

        const signedEmployee = signPrintAuthorization && cacheStore.ClinicSettings.EnableSignedAuthorization
            ? cacheStore.EmployeeCache.firstOrDefault(p => p.ContactFacet_id === signPrintAuthorization?.CreatedByEmployeeId)
            : null;

        if (!isSynced)
            return (requestingStaffId !== currentEmployee.ContactFacetId && currentEmployee.ContactId !== createdbyId)
                && (!signedEmployee || (requestingStaffId !== signedEmployee.ContactFacet_id && signedEmployee.Contact_id !== createdbyId));

        const isRevoked = sharedItem.Status == PortalSyncStatus.Revoked;
        const isSentMessageToPortal = !isRevoked
            && (!sharedItem.IncludeLinkedItem
                && (sharedItem.Status == PortalSyncStatus.Pending
                    || sharedItem.Status == PortalSyncStatus.Viewed));

        return (requestingStaffId !== currentEmployee.ContactFacetId && signedEmployee?.ContactFacet_id !== requestingStaffId)
            && (!isSynced || isRevoked || isSentMessageToPortal);
    }

    public static getSharedItemAttachmentIsVisible(sharedItem: SharedItem): boolean {
        return sharedItem.IncludeLinkedItem
            && this.isStatusVisible(sharedItem.Status);
    }
}

export { PortalHelper };
